import type {
  ApiKeyParams,
  ApiKeysResponse,
  BulkUpdateUserRolesRequest,
  BulkUpdateUserRolesResponse,
  CreateApiKeyParams,
  CreateOrganizationRequest,
  CreateRoleParams,
  CurrentOrganizationResponse,
  Organization,
  OrganizationReferenceDataAccessResponse,
  OrganizationRoleParams,
  UpdateOrganizationParams,
  UpdateRoleParams,
} from '@/types/api/organizations'
import type { ReportBrandingResponse } from '@/types/api/report-branding'
import type {
  AddIpRequest,
  ApiKeyWithSecret,
  IpAllowlistResponse,
  ListAvailablePermissionsResponse,
  ListRolesResponse,
  Role,
} from '@/types/api/roles'

import { TagTypes } from '@/types/api/tag-types'

import { baseApiSlice } from './base-api-slice'

export const organizationsSlice = baseApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Get current organization profile
    getOrganizationProfile: builder.query<CurrentOrganizationResponse, void>({
      query: () => ({
        url: 'org/self/',
        method: 'GET',
      }),
      providesTags: [TagTypes.Organization],
    }),

    // Report branding, already coalesced with the parent organization's
    getReportBranding: builder.query<ReportBrandingResponse, void>({
      query: () => ({
        url: 'org/self/report-branding/',
        method: 'GET',
      }),
      providesTags: [TagTypes.Organization],
    }),

    // Get organization reference data access
    getOrganizationReferenceDataAccess: builder.query<
      OrganizationReferenceDataAccessResponse,
      void
    >({
      query: () => ({
        url: 'org/self/ref-data-access/',
        method: 'GET',
      }),
    }),

    // Create new organization
    createOrganization: builder.mutation<
      Organization,
      CreateOrganizationRequest
    >({
      query: (organization) => ({
        url: 'org/',
        method: 'POST',
        body: organization,
      }),
      invalidatesTags: [TagTypes.Organization],
    }),

    // Update organization
    updateOrganization: builder.mutation<
      Organization,
      UpdateOrganizationParams
    >({
      query: ({ organization_id, updates }) => ({
        url: `org/${organization_id}/`,
        method: 'PUT',
        body: updates,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.Organization, id: arg.organization_id },
              TagTypes.Organization,
            ]
          : [TagTypes.Organization],
    }),

    // Delete organization
    deleteOrganization: builder.mutation<void, string>({
      query: (organization_id) => ({
        url: `org/${organization_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.Organization],
    }),

    // List organization API keys
    listOrganizationAPIKeys: builder.query<ApiKeysResponse, string>({
      query: (organization_id) => ({
        url: `org/${organization_id}/api_key/`,
        method: 'GET',
      }),
      providesTags: [TagTypes.ApiKey],
    }),

    // Create new API key
    createOrganizationAPIKey: builder.mutation<
      ApiKeyWithSecret,
      CreateApiKeyParams
    >({
      query: ({ organization_id, apiKey }) => ({
        url: `org/${organization_id}/api_key/`,
        method: 'POST',
        body: apiKey,
      }),
      invalidatesTags: [TagTypes.ApiKey],
    }),

    // Deactivate API key
    deactivateAPIKey: builder.mutation<void, ApiKeyParams>({
      query: ({ organization_id, api_key_id }) => ({
        url: `org/${organization_id}/api_key/${api_key_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.ApiKey],
    }),

    // Get FIX IP Allowlist
    getIPAllowlist: builder.query<IpAllowlistResponse, void>({
      query: () => ({
        url: 'org/ip/',
        method: 'GET',
      }),
      providesTags: [TagTypes.IPAllowlist],
    }),

    // Add IP to FIX IP Allowlist
    addIPAllowList: builder.mutation<IpAllowlistResponse, AddIpRequest>({
      query: (request) => ({
        url: 'org/ip/',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [TagTypes.IPAllowlist],
    }),

    // Remove IP from FIX IP Allowlist
    removeIPAllowList: builder.mutation<IpAllowlistResponse, string>({
      query: (ip_address) => ({
        url: `org/ip/${ip_address}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.IPAllowlist],
    }),

    // List organization roles
    listOrganizationRoles: builder.query<
      ListRolesResponse,
      { organization_id: string; org_id?: string }
    >({
      query: ({ organization_id, org_id }) => ({
        url: `org/${organization_id}/role/`,
        method: 'GET',
        params: org_id ? { org_id } : undefined,
      }),
      providesTags: [TagTypes.Role],
    }),
    // GET organization role
    getOrganizationRole: builder.query<Role, OrganizationRoleParams>({
      query: ({ organization_id, role_id }) => ({
        url: `org/${organization_id}/role/${role_id}/`,
        method: 'GET',
      }),
      providesTags: [TagTypes.Role],
    }),

    // Create new role
    createOrganizationRole: builder.mutation<Role, CreateRoleParams>({
      query: ({ organization_id, role }) => ({
        url: `org/${organization_id}/role/`,
        method: 'POST',
        body: role,
      }),
      invalidatesTags: [TagTypes.Role],
    }),

    // Update role
    updateOrganizationRole: builder.mutation<Role, UpdateRoleParams>({
      query: ({ organization_id, role_id, updates }) => ({
        url: `org/${organization_id}/role/${role_id}/`,
        method: 'PUT',
        body: updates,
      }),
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, arg) =>
        result
          ? [{ type: TagTypes.Role, id: arg.role_id }, TagTypes.Role]
          : [TagTypes.Role],
    }),

    // Delete role
    deleteOrganizationRole: builder.mutation<void, OrganizationRoleParams>({
      query: ({ organization_id, role_id }) => ({
        url: `org/${organization_id}/role/${role_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.Role],
    }),

    // List available permissions
    listAvailablePermissions: builder.query<
      ListAvailablePermissionsResponse,
      string
    >({
      query: (organization_id) => ({
        url: `org/${organization_id}/available-permissions/`,
        method: 'GET',
      }),
      providesTags: [TagTypes.Permission],
    }),

    // Bulk update user roles
    bulkUpdateUserRoles: builder.mutation<
      BulkUpdateUserRolesResponse,
      { organization_id: string; request: BulkUpdateUserRolesRequest }
    >({
      query: ({ organization_id, request }) => ({
        url: `org/${organization_id}/users/bulk-update-roles/`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [TagTypes.User, TagTypes.Role],
    }),
  }),
})

export const {
  endpoints: {
    getOrganizationProfile,
    getReportBranding,
    getOrganizationReferenceDataAccess,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    listOrganizationAPIKeys,
    createOrganizationAPIKey,
    deactivateAPIKey,
    getIPAllowlist,
    addIPAllowList,
    removeIPAllowList,
    listOrganizationRoles,
    createOrganizationRole,
    updateOrganizationRole,
    deleteOrganizationRole,
    listAvailablePermissions,
    bulkUpdateUserRoles,
  },
  useGetOrganizationProfileQuery,
  useGetReportBrandingQuery,
  useGetOrganizationReferenceDataAccessQuery,

  useListOrganizationRolesQuery,
  useCreateOrganizationRoleMutation,
  useUpdateOrganizationRoleMutation,
  useDeleteOrganizationRoleMutation,
  useListAvailablePermissionsQuery,
  useBulkUpdateUserRolesMutation,
} = organizationsSlice
